import React, { useEffect, useMemo } from 'react';
import { useAgMutation } from '@repo/utils';
import { ActionIcon, Button, Checkbox, Group, Modal, rem, Stack, Textarea, TextInput, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { DatePickerInput } from '@mantine/dates';
import { IconCalendarEvent, IconCircleCheck, IconEdit } from '@tabler/icons-react';
import { Property, useUpdatePropertyMutation } from '@repo/services';

interface CeoVisitModalProps {
  property: Property;
}

/**
 * A modal dialog for updating CEO visit records on a property, supporting marking the visit as completed with a date and updating the visit status.
 */
const CeoVisitModal = ({ property }: CeoVisitModalProps) => {
  const [opened, { open, close }] = useDisclosure(false);

  const form = useForm({
    initialValues: {
      ceoVisitCompletedOn: property?.ceoVisitCompletedOn ? dayjs(property.ceoVisitCompletedOn) : dayjs(),
      ceoVisitCompleted: !!property?.ceoVisitCompleted,
    },
    validate: {
      ceoVisitCompletedOn: (value) => (value ? null : '"Visit Date" is a required field')
    },
  });

  useEffect(() => {
    form.setValues({
      ceoVisitCompletedOn: property?.ceoVisitCompletedOn ? dayjs(property.ceoVisitCompletedOn) : dayjs(),
      ceoVisitCompleted: !!property?.ceoVisitCompleted,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property.id, property.ceoVisitCompletedOn, property.ceoVisitCompleted]);

  const { trigger: triggerUpdateProperty, isLoading: isSaving } = useAgMutation(
    useUpdatePropertyMutation,
    () => {
      notifications.show({
        title: "CEO Visit Updated!",
        message: `CEO visit data for property ${property?.title} was saved successfully!`
      });
      handleClose();
    }
  );

  const handleOpen = () => {
    form.setValues({
      ceoVisitCompletedOn: property?.ceoVisitCompletedOn ? dayjs(property.ceoVisitCompletedOn) : dayjs(),
      ceoVisitCompleted: !!property?.ceoVisitCompleted,
    });
    open();
  };

  const handleClose = () => {
    form.reset();
    close();
  };

  // "Has visited" is now driven solely by the manual completion flag.
  const hasVisited = useMemo(
    () => !!(property?.ceoVisitCompleted && property?.ceoVisitCompletedOn),
    [property?.ceoVisitCompleted, property?.ceoVisitCompletedOn]
  );

  // A scheduled-but-not-yet-completed visit: date set, but the completion flag is false.
  const isScheduledOnly = useMemo(
    () => !!property?.ceoVisitCompletedOn && !property?.ceoVisitCompleted,
    [property?.ceoVisitCompleted, property?.ceoVisitCompletedOn]
  );

  // Visits scheduled in the future cannot be marked complete yet — the visit
  // hasn't physically happened, so completion would be inaccurate.
  const propertyDateIsFuture = useMemo(
    () => !!property?.ceoVisitCompletedOn && dayjs(property.ceoVisitCompletedOn).isAfter(dayjs(), 'day'),
    [property?.ceoVisitCompletedOn]
  );
  const formDateIsFuture = useMemo(
    () => {
      const v = form.values.ceoVisitCompletedOn as dayjs.Dayjs | Date | undefined;
      if (!v) return false;
      const d = (v as dayjs.Dayjs)?.isValid ? (v as dayjs.Dayjs) : dayjs(v as Date);
      return d.isAfter(dayjs(), 'day');
    },
    [form.values.ceoVisitCompletedOn]
  );

  const handleToggleCeoVisitRequired = (checked: boolean) => {
    if (property) {
      triggerUpdateProperty({
        ...property,
        ceoVisitRequired: checked,
        // Clear CEO visit data if not required
        ...(checked ? {} : { ceoVisitCompleted: false, ceoVisitCompletedOn: undefined })
      });
    }
  };

  /**
   * Mark a previously-scheduled CEO visit as complete without opening the
   * edit modal. Keeps the existing date intact and only flips the flag.
   * Disallowed for future-dated visits — the visit hasn't happened yet.
   */
  const handleMarkComplete = () => {
    if (!property?.ceoVisitCompletedOn || propertyDateIsFuture) return;
    triggerUpdateProperty({
      ...property,
      ceoVisitCompleted: true,
    });
  };

  const saveForm = (values: { ceoVisitCompletedOn: dayjs.Dayjs | Date; ceoVisitCompleted: boolean }) => {
    if (property) {
      const dateValue = (values.ceoVisitCompletedOn as dayjs.Dayjs | undefined)?.toDate?.()
        ?? (values.ceoVisitCompletedOn as Date | undefined);
      const dateIsFuture = !!dateValue && dayjs(dateValue).isAfter(dayjs(), 'day');
      triggerUpdateProperty({
        ...property,
        // The date and completion flag are now independent. Setting/changing the
        // date only updates `ceoVisitCompletedOn`; completion is toggled via
        // the explicit "Visit Completed" checkbox. A future-dated visit can
        // never be marked complete.
        ceoVisitCompleted: dateIsFuture ? false : values.ceoVisitCompleted,
        ceoVisitCompletedOn: dateValue,
      });
    }
  };

  const buttonText = useMemo(() => {
    if (hasVisited) {
      return property?.ceoVisitCompletedOn ? dayjs(property.ceoVisitCompletedOn).format('MM-DD-YYYY') : '';
    }
    if (isScheduledOnly) {
      return `Scheduled - ${dayjs(property.ceoVisitCompletedOn).format('MM-DD-YYYY')}`;
    }
    return 'Add CEO Visit';
  }, [property?.ceoVisitCompletedOn, hasVisited, isScheduledOnly]);

  const submitText = useMemo(
    () => (property?.ceoVisitCompletedOn ? 'Update' : 'Submit'),
    [property?.ceoVisitCompletedOn]
  );

  if (!property) return null;

  return (
    <>
      <Modal title='CEO Visit' opened={opened} onClose={handleClose}>
        <form onSubmit={form.onSubmit((values) => saveForm(values))}>
          <Stack gap='sm'>
            <DatePickerInput
              withAsterisk
              firstDayOfWeek={0}
              label={'Visit Date'}
              clearable
              error={form.errors['ceoVisitCompletedOn'] || undefined}
              value={(form.values.ceoVisitCompletedOn as dayjs.Dayjs)?.toDate?.() || (form.values.ceoVisitCompletedOn as unknown as Date) || undefined}
              onChange={(val) => form.setFieldValue('ceoVisitCompletedOn', val ? dayjs(val) : dayjs())}
            />

            <Checkbox
              label='Visit Completed'
              description={formDateIsFuture
                ? 'Cannot be marked complete — the scheduled date is in the future.'
                : 'Check this once the CEO visit has actually taken place.'}
              checked={form.values.ceoVisitCompleted && !formDateIsFuture}
              disabled={formDateIsFuture}
              onChange={(event) => form.setFieldValue('ceoVisitCompleted', event.currentTarget.checked)}
            />

            <Button mt={8} type='submit' loading={isSaving}>
              {submitText}
            </Button>
          </Stack>
        </form>
      </Modal>

      <Stack gap={8}>
        <Checkbox
          label="CEO Visit Required"
          checked={property?.ceoVisitRequired || false}
          onChange={(event) => handleToggleCeoVisitRequired(event.currentTarget.checked)}
          disabled={isSaving}
        />

        {property?.ceoVisitRequired && (
          <>
            {hasVisited || isScheduledOnly ? (
              <Tooltip label={buttonText}>
                <Group gap={0} w={'100%'}>
                  <TextInput
                    w={isScheduledOnly && !propertyDateIsFuture ? '60%' : '75%'}
                    disabled={true}
                    value={buttonText}
                    leftSection={
                      hasVisited
                        ? <IconCircleCheck color={'green'} />
                        : <IconCalendarEvent color={'orange'} />
                    }
                    className='title-input'
                  />
                  <ActionIcon.Group w={isScheduledOnly && !propertyDateIsFuture ? '40%' : '25%'}>
                    {isScheduledOnly && !propertyDateIsFuture && (
                      <Tooltip label='Mark Visit Complete'>
                        <ActionIcon
                          loading={isSaving}
                          onClick={handleMarkComplete}
                          size='lg'
                          color='green'
                          h={36}
                          w={50}
                          aria-label='Mark CEO Visit Complete'
                          style={{ borderRadius: 0 }}
                        >
                          <IconCircleCheck style={{ width: rem(20) }} stroke={1.5} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    <ActionIcon
                      loading={isSaving}
                      onClick={handleOpen}
                      size='lg'
                      color='blue'
                      h={36}
                      w={50}
                      aria-label='Edit CEO Visit'
                      style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                    >
                      <IconEdit style={{ width: rem(20) }} stroke={1.5} />
                    </ActionIcon>
                  </ActionIcon.Group>
                </Group>
              </Tooltip>
            ) : (
              <Tooltip label={buttonText}>
                <Button onClick={handleOpen}>{buttonText}</Button>
              </Tooltip>
            )}
          </>
        )}
      </Stack>
    </>
  );
}

export default CeoVisitModal;